<template>
  <v-container fluid class="bold-listings-page pa-6">
    <v-row>
      <v-col cols="12">
        <v-sheet class="hero-panel pa-6" rounded="lg">
          <div class="hero-header">
            <div>
              <p class="hero-kicker mb-1">Admin Workspace</p>
              <h2 class="hero-title mb-2">Bold Listings</h2>
              <p class="hero-subtitle mb-0">
                Manage featured bold listing placements linked to verified free listings.
              </p>
            </div>
            <div class="hero-actions">
              <v-btn color="white" class="mr-3 hero-filter-btn" @click="toggleAdvanceSearch">
                <v-icon left>mdi-tune-variant</v-icon>
                {{ filtersOpen ? 'Hide Filters' : 'Show Filters' }}
              </v-btn>
              <v-btn
                v-if="modelPermissions.create"
                color="secondary"
                dark
                class="hero-create-btn"
                @click="openCreateDialog"
              >
                <v-icon left>mdi-plus</v-icon>
                Create Listing
              </v-btn>
            </div>
          </div>

          <div class="hero-metrics mt-5">
            <div class="metric-card">
              <p class="metric-label mb-1">Total Records</p>
              <p class="metric-value mb-0">{{ totalRecords }}</p>
            </div>
            <div class="metric-card">
              <p class="metric-label mb-1">Rows Loaded</p>
              <p class="metric-value mb-0">{{ loadedRecords }}</p>
            </div>
            <div class="metric-card">
              <p class="metric-label mb-1">Filters Active</p>
              <p class="metric-value mb-0">{{ hasActiveFilters ? 'Yes' : 'No' }}</p>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-expand-transition>
          <v-card v-show="filtersOpen" class="filters-card mb-5" outlined>
            <v-card-text class="pb-2">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.lazy="email"
                    clearable
                    clear-icon="mdi-close-circle"
                    prepend-inner-icon="mdi-email-outline"
                    label="Filter by Email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <date-picker v-model="fromDate" label="From"></date-picker>
                </v-col>
                <v-col cols="12" md="4">
                  <date-picker v-model="toDate" label="To"></date-picker>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="4">
                  <select-categories
                    v-model="category"
                    label="Category"
                  ></select-categories>
                </v-col>
                <v-col cols="12" md="4">
                  <select-suburbs
                    v-model="suburb"
                    label="Suburb"
                  ></select-suburbs>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-end">
                  <v-btn
                    outlined
                    color="primary"
                    :disabled="!hasActiveFilters"
                    @click="clearFilters"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Clear Filters
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-expand-transition>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="table-card" outlined>
          <v-data-table
            :server-items-length="bold_listings.total"
            :loading="loading"
            :headers="headers"
            :items="bold_listings.data"
            :options.sync="options"
            :items-per-page="5"
            class="bold-listings-table"
            loading-text="Loading bold listings..."
          >
            <template v-slot:item.business_name="{ item }">
              <div class="name-cell">
                <v-avatar color="#dcfce7" size="34">
                  <v-icon small color="#166534">mdi-lightning-bolt-outline</v-icon>
                </v-avatar>
                <div class="ml-3">
                  <p class="name-main mb-0">{{ item.business_name || '-' }}</p>
                  <p class="name-sub mb-0">Listing ID: {{ item.id }}</p>
                </div>
              </div>
            </template>

            <template v-slot:item.email="{ item }">
              <a
                v-if="item.email"
                :href="`mailto:${item.email}`"
                class="email-link"
              >
                <v-icon x-small class="mr-1">mdi-email</v-icon>
                {{ item.email }}
              </a>
              <span v-else class="grey--text text--darken-1">No email</span>
            </template>

            <template v-slot:item.start_date="{ item }">
              <v-chip small outlined color="#0f766e">
                {{ item.start_date || 'Not set' }}
              </v-chip>
            </template>

            <template v-slot:item.end_date="{ item }">
              <v-chip small outlined color="#c2410c">
                {{ item.end_date || 'Not set' }}
              </v-chip>
            </template>

            <template v-slot:item.verified="{ item }">
              <v-chip
                small
                :color="item.verified === 1 ? '#dcfce7' : '#fee2e2'"
                :text-color="item.verified === 1 ? '#166534' : '#991b1b'"
              >
                {{ item.verified === 1 ? 'Verified' : 'Unverified' }}
              </v-chip>
            </template>

            <template v-slot:item.free_listing_active="{ item }">
              <v-chip
                small
                :color="item.free_listing_active === 1 ? '#dbeafe' : '#f3f4f6'"
                :text-color="item.free_listing_active === 1 ? '#1e3a8a' : '#4b5563'"
              >
                {{ item.free_listing_active === 1 ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-if="modelPermissions.edit" v-slot:item.edit="{ item }">
              <v-btn icon color="primary" @click="editItem(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>

            <template v-if="modelPermissions.delete" v-slot:item.delete="{ item }">
              <v-btn icon color="error" @click="deleteItem(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="py-8 text-center">
                <v-icon color="grey" large class="mb-2">mdi-database-off-outline</v-icon>
                <p class="mb-0 grey--text text--darken-1">No bold listings found.</p>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog persistent v-model="dialog">
      <bold-listing-dialog
        v-if="dialog"
        :bold_listings="bold_listings"
        :item="item"
        :close="close"
      ></bold-listing-dialog>
    </v-dialog>
  </v-container>
</template>

<script>
import BoldListingDialog from './BoldListingDialog'
import DatePicker from '../../../components/DatePicker'
import SelectSuburbs from '../../../components/SelectSuburbs'
import SelectCategories from '../../../components/SelectCategories'
import axios from '../../../utils/axios'
import { mapGetters } from 'vuex'

export default {
  name: 'BoldListingIndex',
  components: {
    BoldListingDialog,
    DatePicker,
    SelectCategories,
    SelectSuburbs
  },
  data() {
    return {
      filtersOpen: true,
      dialog: false,
      loading: false,
      headers: [
        { text: 'Business Name', align: 'start', value: 'business_name' },
        { text: 'Email', align: 'start', value: 'email' },
        { text: 'Start Date', align: 'center', value: 'start_date', sortable: false },
        { text: 'End Date', align: 'center', value: 'end_date', sortable: false },
        { text: 'Verified', align: 'center', value: 'verified', sortable: false },
        {
          text: 'Status',
          align: 'center',
          value: 'free_listing_active',
          sortable: false
        },
        { text: 'Edit', align: 'center', value: 'edit', sortable: false },
        { text: 'Delete', align: 'center', value: 'delete', sortable: false }
      ],
      bold_listings: {
        data: [],
        total: 0
      },
      options: {},
      item: {},
      email: '',
      category: '',
      suburb: '',
      fromDate: '',
      toDate: ''
    }
  },
  computed: {
    ...mapGetters(['permissions/getModelPermissions']),
    modelPermissions() {
      return this['permissions/getModelPermissions']('bold_listing')
    },
    totalRecords() {
      return this.bold_listings && this.bold_listings.total ? this.bold_listings.total : 0
    },
    loadedRecords() {
      return this.bold_listings &&
        this.bold_listings.data &&
        this.bold_listings.data.length
        ? this.bold_listings.data.length
        : 0
    },
    hasActiveFilters() {
      return (
        this.email !== '' ||
        this.category !== '' ||
        this.suburb !== '' ||
        this.fromDate !== '' ||
        this.toDate !== ''
      )
    }
  },
  methods: {
    toggleAdvanceSearch() {
      this.filtersOpen = !this.filtersOpen
    },
    clearFilters() {
      this.email = ''
      this.category = ''
      this.suburb = ''
      this.fromDate = ''
      this.toDate = ''
    },
    close() {
      this.item = {}
      this.dialog = false
    },
    openCreateDialog() {
      this.item = {}
      this.dialog = true
    },
    editItem(item) {
      this.item = item
      this.dialog = true
    },
    getBoldListings() {
      const page = this.options.page || 1
      const itemsPerPage = this.options.itemsPerPage || 5
      this.loading = true
      axios
        .get(
          encodeURI(
            `api/admin/bold-listings?page=${page}&itemsPerPage=${itemsPerPage}&email=${this.email}&category=${this.category}&suburb=${this.suburb}&fromDate=${this.fromDate}&endDate=${this.toDate}`
          )
        )
        .then(res => {
          this.bold_listings = res.data
        })
        .catch(() => {
          this.bold_listings = { data: [], total: 0 }
        })
        .finally(() => {
          this.loading = false
        })
    },
    deleteItem(item) {
      if (confirm(`Confirm Delete...${item.id}`)) {
        axios
          .delete(`api/admin/bold-listings/${item.id}`)
          .then(res => {
            if (res.data === 1) {
              this.getBoldListings()
            }
          })
          .catch(() => {})
      }
    }
  },
  watch: {
    options: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    },
    dialog: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    },
    email: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    },
    category: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    },
    suburb: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    },
    fromDate: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    },
    toDate: {
      handler() {
        this.getBoldListings()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.bold-listings-page {
  background: linear-gradient(180deg, #f0fdf4 0%, #eff6ff 100%);
  min-height: calc(100vh - 120px);
}

.hero-panel {
  background:
    radial-gradient(circle at 88% 12%, rgba(16, 185, 129, 0.28) 0, transparent 34%),
    linear-gradient(120deg, #064e3b 0%, #065f46 45%, #0f766e 100%);
  color: #ffffff;
}

.hero-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.hero-kicker {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.78);
}

.hero-title {
  font-size: 2rem;
  line-height: 1.1;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.82);
  max-width: 620px;
}

.hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.hero-filter-btn {
  color: #065f46;
  font-weight: 600;
}

.hero-create-btn {
  font-weight: 600;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.metric-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
}

.metric-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.8);
}

.metric-value {
  font-size: 1.45rem;
  font-weight: 700;
}

.filters-card,
.table-card {
  border-color: #cce9dd !important;
  border-radius: 14px;
}

.bold-listings-table ::v-deep thead tr th {
  font-weight: 700 !important;
  color: #065f46 !important;
  background: #ecfdf5;
}

.name-cell {
  display: flex;
  align-items: center;
}

.name-main {
  font-weight: 600;
  color: #1f2937;
}

.name-sub {
  color: #6b7280;
  font-size: 0.72rem;
}

.email-link {
  color: #0f766e;
  text-decoration: none;
  font-weight: 500;
}

.email-link:hover {
  text-decoration: underline;
}

@media (max-width: 960px) {
  .bold-listings-page {
    padding: 16px !important;
  }

  .hero-title {
    font-size: 1.6rem;
  }
}
</style>
